import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bell,
  ChevronRight,
  CircleHelp,
  Crown,
  Mail,
  MessageCircle,
  Palette,
  Settings,
  UserRound,
} from "lucide-react";
import { AppHeader } from "@/components/site/AppHeader";

export const Route = createFileRoute("/_authenticated/customer/more")({
  head: () => ({
    meta: [
      { title: "More — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CustomerMore,
});

const sections = [
  {
    title: "Your account",
    rows: [
      { to: "/account" as const, icon: UserRound, title: "Profile & account", detail: "Your details, photo, homes and security", tone: "blue" },
      { to: "/plans" as const, icon: Crown, title: "Membership", detail: "See your current plan and benefits", tone: "gold" },
      { to: "/personalise" as const, icon: Palette, title: "Personalise Shields", detail: "Adjust your app experience", tone: "purple", search: { experience: "customer" as const } },
    ],
  },
  {
    title: "Keep in touch",
    rows: [
      { to: "/messages" as const, icon: MessageCircle, title: "Messages", detail: "Conversations linked to your jobs", tone: "green" },
      { to: "/inbox" as const, icon: Mail, title: "Inbox", detail: "Updates and account messages", tone: "blue" },
      { to: "/notifications" as const, icon: Bell, title: "Notifications", detail: "Things that need your attention", tone: "gold" },
    ],
  },
  {
    title: "Help & settings",
    rows: [
      { to: "/help" as const, icon: CircleHelp, title: "Help & support", detail: "Guidance and ways to get help", tone: "blue" },
      { to: "/account" as const, icon: Settings, title: "Security & data", detail: "Password, privacy and account controls", tone: "navy" },
    ],
  },
] as const;

function CustomerMore() {
  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <header>
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#c99522]">Everything else</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">More</h1>
          <p className="mt-2 text-sm leading-6 text-[#66758d]">
            Account, messages, membership and help — kept out of the way until you need them.
          </p>
        </header>

        <div className="mt-6 space-y-6">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="mb-2 text-sm font-extrabold text-[#43546b]">{section.title}</h2>
              <div className="overflow-hidden rounded-2xl border border-[#dfe7ef] bg-white shadow-[0_8px_24px_rgba(6,27,58,.035)]">
                {section.rows.map((row, index) => {
                  const Icon = row.icon;
                  const inner = (
                    <>
                      <span
                        className={
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl " +
                          (row.tone === "gold"
                            ? "bg-[#fff1c7] text-[#b37a12]"
                            : row.tone === "purple"
                              ? "bg-[#f1ebff] text-[#6c46d8]"
                              : row.tone === "green"
                                ? "bg-[#eaf8f0] text-[#278d60]"
                                : row.tone === "navy"
                                  ? "bg-[#eef2f7] text-[#0b3568]"
                                  : "bg-[#eaf3ff] text-[#2c69c7]")
                        }
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <strong className="block text-[#061b3a]">{row.title}</strong>
                        <small className="mt-0.5 block text-sm text-[#66758d]">{row.detail}</small>
                      </span>
                      <ChevronRight className="h-5 w-5 shrink-0 text-[#738198]" />
                    </>
                  );
                  const cls = "flex min-h-17 items-center gap-3 px-4 py-3 " + (index ? "border-t border-[#edf1f5]" : "");
                  return "search" in row ? (
                    <Link key={row.title} to={row.to} search={row.search} className={cls}>{inner}</Link>
                  ) : (
                    <Link key={row.title} to={row.to} className={cls}>{inner}</Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
